"use client";
// «Elige tu avatar», como al elegir perfil en una plataforma de video: las aves de siempre, los que agregó Soporte, o tu propia foto.
// La foto se recorta en cuadrado y se reduce a 256 px en el teléfono antes de subirse.
import { useEffect, useRef, useState } from "react";
import { pedirCuenta } from "@/components/CuentaVerificada";

type Avatar = { id: string; nombre: string; url: string; propio: boolean };

/** Recorta al centro en cuadrado y reduce a 256 px (WebP). */
export function fotoCuadrada(archivo: File, lado = 256): Promise<string> {
  return new Promise((ok, mal) => {
    const url = URL.createObjectURL(archivo), img = new Image();
    img.onload = () => {
      const m = Math.min(img.width, img.height), cv = document.createElement("canvas"); cv.width = lado; cv.height = lado;
      cv.getContext("2d")!.drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, lado, lado);
      URL.revokeObjectURL(url); ok(cv.toDataURL("image/webp", 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); mal(new Error("No pudimos leer esa imagen. Prueba con otra.")); };
    img.src = url;
  });
}

export function ElegirAvatar({ actual, cerrar, cambiado }: { actual: string | null; cerrar: () => void; cambiado: (url: string | null) => void }) {
  const [lista, setLista] = useState<Avatar[] | null>(null), [ocupado, setOcupado] = useState<string | null>(null), [error, setError] = useState("");
  const caja = useRef<HTMLDivElement>(null);
  useEffect(() => { pedirCuenta("/api/avatares").then((d: { galeria: Avatar[] }) => setLista(d.galeria)).catch((e) => setError(e instanceof Error ? e.message : "No se pudo cargar")); }, []);
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
    document.addEventListener("keydown", tecla); caja.current?.querySelector<HTMLElement>("button, label")?.focus();
    return () => document.removeEventListener("keydown", tecla);
  }, [cerrar]);
  const guardar = async (clave: string, cuerpo: object) => {
    setOcupado(clave); setError("");
    try { const r: { avatar: string | null } = await pedirCuenta("/api/avatares/mio", cuerpo, "PUT"); cambiado(r.avatar); cerrar(); }
    catch (e) { setError(e instanceof Error ? e.message : "No se pudo guardar"); } finally { setOcupado(null); }
  };
  const subir = async (f: File | undefined) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { setError("Elige una imagen (JPG, PNG o WebP)."); return; }
    try { await guardar("foto", { foto: await fotoCuadrada(f) }); } catch (e) { setError(e instanceof Error ? e.message : "No se pudo subir"); }
  };
  return <div className="ea-velo" onClick={cerrar}>
    <div className="ea-caja" role="dialog" aria-modal="true" aria-labelledby="ea-t" ref={caja} onClick={(e) => e.stopPropagation()}>
      <header><h2 id="ea-t">Elige tu avatar</h2><p>Así te ve el equipo en la nómina, el rol y los avisos.</p></header>
      {error && <p role="alert" className="ea-error">{error}</p>}
      <ul className="ea-rejilla">
        <li><label className={`ea-op ea-subir${ocupado === "foto" ? " ocupado" : ""}`}>
          <input type="file" accept="image/*" onChange={(e) => { void subir(e.target.files?.[0]); e.target.value = ""; }} disabled={!!ocupado} />
          <span className="ea-circulo"><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4zM12 16.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z" /></svg></span>
          <span className="ea-nombre">{ocupado === "foto" ? "Subiendo…" : "Subir mi foto"}</span></label></li>
        {lista === null && Array.from({ length: 8 }, (_, i) => <li key={i}><span className="ea-op"><span className="ea-circulo ea-cargando" /><span className="ea-nombre">&nbsp;</span></span></li>)}
        {lista?.map((a) => { const elegido = actual === a.url; return <li key={a.id}>
          <button type="button" className="ea-op" aria-pressed={elegido} disabled={!!ocupado} onClick={() => void guardar(a.id, { galeriaId: a.id })}>
            <span className="ea-circulo"><img src={a.url} alt="" loading="lazy" width={96} height={96} />{elegido && <span className="ea-check" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg></span>}</span>
            <span className="ea-nombre">{a.nombre}</span>
          </button></li>; })}
      </ul>
      <footer>
        {actual && <button type="button" className="ea-texto" disabled={!!ocupado} onClick={() => void guardar("quitar", { galeriaId: null })}>Usar el ave de mi apodo</button>}
        <button type="button" className="ea-cerrar" onClick={cerrar}>Listo</button>
      </footer>
    </div>
  </div>;
}
